import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Mail, RefreshCw, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Register() {
  const navigate = useNavigate();
  const { register, resendVerification } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // After a successful signup we show a "Check your email" screen INSTEAD of
  // silently redirecting to /. This makes the verification step discoverable
  // and drives higher completion rates.
  const [signedUpEmail, setSignedUpEmail] = useState(null);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password) || password.length < 8) {
      toast.error('Password must be at least 8 characters with uppercase, lowercase, and a number');
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name);
      setSignedUpEmail(email);
      toast.success('Account created — check your inbox!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerification();
      toast.success('Verification email sent again.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  if (signedUpEmail) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md text-center"
          data-testid="check-email-screen"
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-lime/10 border border-lime/30 flex items-center justify-center">
            <Mail className="w-8 h-8 text-lime" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-3">Check your email</h1>
          <p className="text-[#A1A1AA] mb-6">
            We&apos;ve sent a verification link to<br />
            <span className="text-white font-medium" data-testid="check-email-address">{signedUpEmail}</span>
          </p>

          <div className="bg-surface border border-[#262626] rounded-xl p-5 mb-6 text-left space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 text-lime shrink-0 mt-0.5" />
              <p className="text-sm text-[#A1A1AA]">Click the link in that email to verify your address.</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 text-lime shrink-0 mt-0.5" />
              <p className="text-sm text-[#A1A1AA]">The link expires in 24 hours.</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 text-lime shrink-0 mt-0.5" />
              <p className="text-sm text-[#A1A1AA]">You&apos;ll need a verified email to order dubplates or message artists.</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={handleResend}
              disabled={resending}
              className="flex-1"
              data-testid="check-email-resend-btn"
            >
              {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Resend email
            </Button>
            <Button
              onClick={() => navigate('/')}
              className="flex-1 btn-primary"
              data-testid="check-email-continue-btn"
            >
              Continue to Tune4Tune
            </Button>
          </div>

          <p className="text-xs text-[#666] mt-6">
            Didn&apos;t get the email? Check spam, or {' '}
            <Link to="/settings" className="text-lime hover:underline">update your email in Settings</Link>.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-l from-[#050505] to-transparent z-10" />
        <img
          src="https://images.unsplash.com/photo-1644767479973-a053733ef283?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Studio"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img 
              src="/logo.png" 
              alt="Tune4Tune" 
              className="h-12 w-auto"
            />
            <span className="font-heading font-bold text-2xl">Tune4Tune</span>
          </Link>

          <h1 className="font-heading text-3xl font-bold mb-2">Create Account</h1>
          <p className="text-[#A1A1AA] mb-8">Join Tune4Tune and get exclusive dubplates from top artists</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="input-dark w-full"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="input-dark w-full"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-dark w-full pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-[#666]">Min 8 characters with uppercase, lowercase, and a number</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
            <p className="mt-4 text-center text-[11px] text-[#666] leading-relaxed" data-testid="register-legal-consent">
              By creating an account you agree to our{' '}
              <Link to="/terms" className="text-lime hover:underline">Terms of Service</Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-lime hover:underline">Privacy Policy</Link>.
            </p>
          </form>

          <p className="mt-8 text-center text-[#A1A1AA]">
            Already have an account?{' '}
            <Link to="/login" className="text-lime hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
